import { ISS_CLASSIFICATION_LABELS } from '../../helpers/sectorInsights/fetchInsightCells'

/**
 * Geordende ISS 2.0-classificatietreden (zwakst → sterkst) met de vereiste per
 * trede en de externe zichtbaarheid. De drempels spiegelen de beslisboom in
 * functions/insightStrength/classifyInsight.js + parameters.js — wijzig samen.
 *
 * @typedef {object} ClassificationTier
 * @property {string} key - Machinewaarde van de classificatie
 * @property {string} requirement - Wat een cel nodig heeft voor deze trede
 * @property {string} visibility - Externe zichtbaarheid van de trede
 */
/** @type {Array<ClassificationTier>} */
const CLASSIFICATION_TIERS = [
    {
        key: 'verschil_niet_aangetoond',
        requirement: 'De beslisboom gaf een toonbare trede, maar de groei is niet aantoonbaar groter dan bij organisaties die dit patroon niet hebben (minimaal 3 tegen 3, p ≤ 0,05).',
        visibility: 'Niet toonbaar',
    },
    {
        key: 'weerlegd',
        requirement: 'Replicatie (P3) draaide om: in data van ná de ontdekking wees het patroon de andere kant op. Degradeert ongeacht bewijskracht en volume.',
        visibility: 'Niet toonbaar',
    },
    {
        key: 'onvoldoende_onderbouwd',
        requirement: 'Databasis (P2) niet gehaald, óf te weinig bewijskracht (B < 0,55) zonder groot effect.',
        visibility: 'Niet toonbaar',
    },
    {
        key: 'opvallend_signaal',
        requirement: 'P2 gehaald, groot effect (E ≥ 0,6), maar bewijskracht nog te laag (B < 0,55).',
        visibility: 'Alleen intern',
    },
    {
        key: 'indicatief',
        requirement: 'P2 gehaald, bewijskracht B ≥ 0,55, en meer groei dan bij organisaties zonder dit patroon (minimaal 3 tegen 3, p ≤ 0,05).',
        visibility: 'Toonbaar',
    },
    {
        key: 'indicatief_nieuw',
        requirement: 'Bewijskracht B ≥ 0,75 op het hoogste databasis-niveau en meer groei dan zonder (5 tegen 5), maar replicatie (P3) nog niet toetsbaar.',
        visibility: 'Toonbaar',
    },
    {
        key: 'sterk_onderbouwd',
        requirement: 'Bewijskracht B ≥ 0,75, replicatie (P3) bevestigd, en meer groei dan bij organisaties zonder dit patroon (5 tegen 5).',
        visibility: 'Toonbaar',
    },
]

// GEEN gespiegelde drempel meer. Hier stond `P2_MIN_LEVEL = 3`, een kopie van
// parameters.js die bij de staffelverlaging van ISS 2026.8 (poort naar niveau 2)
// niet meeging: een cel op niveau 2 haalt de poort wél, maar deze popup zei
// 'Niet gehaald'. De uitkomst van de poort staat op het celdocument zelf
// (`gates.p2.passed`, sinds 2026.8) — dat is de bron.
// AI-CONTEXT: See docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment

/**
 * @typedef {object} CellContrast
 * @property {string} status - berekend | te-klein | geen-zonder-groep | zonder-onbekend
 * @property {number} kWith - organisaties met dit patroon
 * @property {number} kWithout - organisaties zonder (wel iets vastgelegd op de dimensie)
 * @property {number} kUnknown - organisaties die op de dimensie niets vastlegden
 * @property {number|null} pPerm - eenzijdige permutatie-p, null als er niet getoetst is
 * @property {boolean} passed - verschil aangetoond
 */

/**
 * Eén regel over de vergelijking met organisaties zonder dit patroon. De
 * uitkomst komt van de server (`contrast` op het celdocument); hier wordt
 * niets herrekend.
 * @param {CellContrast|null|undefined} contrast
 * @returns {string}
 */
const contrastText = (contrast) => {
    if (!contrast) return '—'
    const onbekend = contrast.kUnknown > 0 ? ` (${contrast.kUnknown} onbekend)` : ''
    const groepen = `${contrast.kWith} met · ${contrast.kWithout} zonder${onbekend}`
    if (contrast.status !== 'berekend' || typeof contrast.pPerm !== 'number') return `${groepen} · niet te toetsen`
    const p = contrast.pPerm.toFixed(2).replace('.', ',')
    return `${groepen} · p = ${p} · ${contrast.passed ? 'aangetoond' : 'niet aangetoond'}`
}

/**
 * Uitleg-popup voor de classificatie van één inzicht-cel: markeert de huidige
 * trede en toont de treden eronder en erboven, plus de bindende poorten van
 * deze cel (P2-databasis, bewijskracht B, P3-replicatie). Analoog aan
 * StrengthExplanation voor de Sterkte-kolom.
 *
 * @param {object} props
 * @param {string} props.classification - Huidige classificatie (machinewaarde), markeert de bijbehorende rij
 * @param {number|null} props.B - Bewijskracht B van de cel
 * @param {number} props.p2Level - P2-celniveau (0-3)
 * @param {boolean} [props.p2Passed] - Of de cel de P2-poort haalt, zoals de berekening hem zette. Zonder dit veld (oudere celdocumenten) valt de weergave terug op 'onbekend' in plaats van op een gespiegelde drempel.
 * @param {string} [props.p3Status] - P3-replicatiestatus (bevestigd|nog_niet_toetsbaar|weerlegd)
 * @param {CellContrast|null} [props.contrast] - De vergelijking met organisaties zonder dit patroon (ISS 2026.10); ontbreekt bij oudere cellen
 */
const ClassificationExplanation = ({ classification, B, p2Level, p2Passed, p3Status, contrast }) => (
    <div className='bd-strength-explanation'>
        <p className='bd-strength-explanation-intro'>
            De classificatie volgt een beslisboom: eerst de databasis (P2: genoeg organisaties, respondenten en spreiding), dan de bewijskracht B, en ten slotte replicatie (P3). Daarna moet de groei groter zijn dan bij organisaties zonder dit patroon (contrast). Het huidige niveau staat gemarkeerd, met de niveaus eronder en erboven.
        </p>
        <div className='bd-strength-current-values'>
            <div className='bd-strength-current-value'>
                <span className='bd-strength-current-value-label'>Databasis (P2)</span>
                <span className='bd-strength-current-value-number'>{typeof p2Passed === 'boolean' ? (p2Passed ? 'Gehaald' : 'Niet gehaald') : '—'}</span>
            </div>
            <div className='bd-strength-current-value'>
                <span className='bd-strength-current-value-label'>Bewijskracht (B)</span>
                <span className='bd-strength-current-value-number'>{typeof B === 'number' ? B.toFixed(2) : '—'}</span>
            </div>
            <div className='bd-strength-current-value'>
                <span className='bd-strength-current-value-label'>Replicatie (P3)</span>
                <span className='bd-strength-current-value-number'>{p3Status ? p3Status.replace(/_/g, ' ') : '—'}</span>
            </div>
            <div className='bd-strength-current-value'>
                <span className='bd-strength-current-value-label'>Met/zonder (contrast)</span>
                <span className='bd-strength-current-value-number'>{contrastText(contrast)}</span>
            </div>
        </div>
        <table className='bd-strength-explanation-table'>
            <thead>
                <tr>
                    <th>Niveau</th>
                    <th>Vereiste</th>
                    <th>Zichtbaarheid</th>
                </tr>
            </thead>
            <tbody>
                {CLASSIFICATION_TIERS.map((tier) => (
                    <tr
                        key={tier.key}
                        className={tier.key === classification ? 'bd-strength-row--current' : ''}
                    >
                        <td><strong>{ISS_CLASSIFICATION_LABELS[tier.key] || tier.key}</strong></td>
                        <td>{tier.requirement}</td>
                        <td>{tier.visibility}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
)

export default ClassificationExplanation
